/**
 * Analytics Event Tracking
 * 핵심 이벤트 추적 및 전환 퍼널 측정
 */

// Analytics 이벤트 타입
export type AnalyticsEvent =
  | "category_click"
  | "filter_apply"
  | "detail_view"
  | "chat_start"
  | "chat_send"
  | "kakao_share"
  | "saved_search_create"
  | "ad_impression"
  | "ad_click"
  | "phone_click"
  | "business_impression"
  | "business_detail_view"
  | "directions_click"
  | "website_click"
  | "inquiry_submitted"
  | "owner_replied"
  | "lead_marked_won"
  | "bookmark"
  | "comment_create"
  | "report_create";

export interface AnalyticsEventData {
  event: AnalyticsEvent;
  contentType?: string;
  contentId?: string;
  category?: string;
  region?: string;
  tags?: string[];
  metadata?: Record<string, any>;
  searchQuery?: string;
  categoryId?: number;
  suburb?: string;
  position?: number;
  placementType?: "organic" | "sponsored";
}

function getAnalyticsSessionId(): string {
  if (typeof window === "undefined") return "server";
  const key = "ozk_analytics_session_id";
  const existing = window.sessionStorage.getItem(key);
  if (existing) return existing;
  const created = crypto.randomUUID();
  window.sessionStorage.setItem(key, created);
  return created;
}

/**
 * Analytics 이벤트 전송
 */
export async function trackEvent(data: AnalyticsEventData): Promise<void> {
  try {
    // Google Analytics 4 (if configured)
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", data.event, {
        content_type: data.contentType,
        content_id: data.contentId,
        category: data.category,
        region: data.region,
        tags: data.tags?.join(","),
        ...data.metadata,
      });
    }

    // Custom Analytics API
    await fetch("/api/analytics/track", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...data,
        eventId: crypto.randomUUID(),
        sessionId: getAnalyticsSessionId(),
        timestamp: Date.now(),
        url: window.location.pathname,
        referrer: document.referrer,
      }),
    }).catch((err) => {
      // Silent fail for analytics
      console.debug("Analytics tracking failed:", err);
    });
  } catch (error) {
    // Silent fail for analytics
    console.debug("Analytics error:", error);
  }
}

/**
 * 전환 이벤트 추적
 */
export async function trackConversion(
  type: "detail_to_chat" | "detail_to_phone" | "detail_to_kakao",
  contentId: string,
  contentType: string,
  metadata?: Record<string, any>
): Promise<void> {
  await trackEvent({
    event: "chat_start",
    contentType,
    contentId,
    metadata: {
      conversion_type: type,
      ...metadata,
    },
  });
}

/**
 * 카테고리 클릭 추적
 */
export function trackCategoryClick(category: string, region?: string): void {
  trackEvent({
    event: "category_click",
    category,
    region,
  });
}

/**
 * 필터 적용 추적
 */
export function trackFilterApply(
  filters: Record<string, any>,
  resultCount: number
): void {
  trackEvent({
    event: "filter_apply",
    metadata: {
      filters,
      result_count: resultCount,
    },
  });
}

/**
 * 상세 보기 추적
 */
export function trackDetailView(
  contentType: string,
  contentId: string,
  category?: string
): void {
  trackEvent({
    event: "detail_view",
    contentType,
    contentId,
    category,
  });
}

/**
 * 채팅 시작 추적
 */
export function trackChatStart(
  contentType: string,
  contentId: string,
  sellerId: string
): void {
  trackEvent({
    event: "chat_start",
    contentType,
    contentId,
    metadata: {
      seller_id: sellerId,
    },
  });
}

/**
 * 광고 클릭 추적
 */
export function trackAdClick(
  adId: string,
  placement: string,
  contentType?: string,
  contentId?: string
): void {
  trackEvent({
    event: "ad_click",
    contentType,
    contentId,
    metadata: {
      ad_id: adId,
      placement,
    },
  });
}

/**
 * 광고 노출 추적
 */
export function trackAdImpression(
  adId: string,
  placement: string
): void {
  trackEvent({
    event: "ad_impression",
    metadata: {
      ad_id: adId,
      placement,
    },
  });
}
